/**
 * Panel de Filtros del Visor - V05.3
 * PTEL - Sistema de Normalización de Coordenadas
 */

import React, { memo, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Filter, X } from 'lucide-react';
import type { InfrastructureType } from '../../../types/infrastructure';
import type { MarkerGroup } from '../../../constants/markerConfig';
import type { FilterPanelProps } from './types';
import { DEFAULT_FILTER_STATE } from './types';
import { filterInfrastructures, calculateFilterStats, countByTypeUnfiltered, getTypesInGroup } from './filterUtils';
import { SearchFilter } from './SearchFilter';
import { ScoreFilter } from './ScoreFilter';
import { TypeFilter } from './TypeFilter';
import { FilterStats } from './FilterStats';
import './FilterPanel.css';

export const FilterPanel = memo<FilterPanelProps>(function FilterPanel({
  data,
  filters,
  onFiltersChange,
  position = 'left',
  collapsible = true,
  defaultCollapsed = false,
  showStats = true,
  showSearch = true,
  className = '',
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Calcular datos filtrados
  const filteredData = useMemo(
    () => filterInfrastructures(data, filters),
    [data, filters]
  );

  // Estadísticas
  const stats = useMemo(
    () => calculateFilterStats(data, filteredData),
    [data, filteredData]
  );

  // Conteos sin filtrar (para mostrar totales por tipo)
  const unfilteredCounts = useMemo(
    () => countByTypeUnfiltered(data),
    [data]
  );

  // Handlers
  const handleSearchChange = useCallback((text: string) => {
    onFiltersChange({ ...filters, searchText: text });
  }, [filters, onFiltersChange]);

  const handleScoreChange = useCallback((min: number, max: number) => {
    onFiltersChange({ ...filters, minScore: min, maxScore: max });
  }, [filters, onFiltersChange]);

  const handleToggleType = useCallback((type: InfrastructureType) => {
    const exists = filters.selectedTypes.includes(type);
    onFiltersChange({
      ...filters,
      selectedTypes: exists
        ? filters.selectedTypes.filter(t => t !== type)
        : [...filters.selectedTypes, type],
    });
  }, [filters, onFiltersChange]);

  const handleToggleGroup = useCallback((group: MarkerGroup) => {
    const typesInGroup = getTypesInGroup(group);
    const allSelected = typesInGroup.every(t => filters.selectedTypes.includes(t));
    
    onFiltersChange({
      ...filters,
      selectedTypes: allSelected
        ? filters.selectedTypes.filter(t => !typesInGroup.includes(t))
        : [...new Set([...filters.selectedTypes, ...typesInGroup])],
    });
  }, [filters, onFiltersChange]);

  const handleToggleGroupExpanded = useCallback((group: MarkerGroup) => {
    onFiltersChange({
      ...filters,
      expandedGroups: filters.expandedGroups.includes(group)
        ? filters.expandedGroups.filter(g => g !== group)
        : [...filters.expandedGroups, group],
    });
  }, [filters, onFiltersChange]);

  const handleReset = useCallback(() => {
    onFiltersChange(DEFAULT_FILTER_STATE);
  }, [onFiltersChange]);

  // Botón colapsar
  if (collapsible && isCollapsed) {
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={() => setIsCollapsed(false)}
        className={`
          ptel-filter-panel-collapsed
          fixed top-20 z-[1000]
          ${position === 'left' ? 'left-4' : 'right-4'}
          bg-stone-900 border-stone-700 hover:bg-stone-800
        `}
        aria-label="Abrir filtros"
      >
        <Filter className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Card className={`
      ptel-filter-panel
      fixed top-20 z-[1000] w-72 max-h-[calc(100vh-6rem)] overflow-y-auto
      ${position === 'left' ? 'left-4' : 'right-4'}
      bg-stone-900 border-stone-700
      ${className}
    `}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium text-stone-200 flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filtros
        </CardTitle>
        {collapsible && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(true)}
            className="h-8 w-8 text-stone-400 hover:text-stone-200"
            aria-label="Cerrar filtros"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Búsqueda */}
        {showSearch && (
          <SearchFilter
            value={filters.searchText}
            onChange={handleSearchChange}
          />
        )}

        {/* Filtro por tipo */}
        <TypeFilter
          selectedTypes={filters.selectedTypes}
          expandedGroups={filters.expandedGroups}
          counts={unfilteredCounts}
          onToggleType={handleToggleType}
          onToggleGroup={handleToggleGroup}
          onToggleGroupExpanded={handleToggleGroupExpanded}
        />

        {/* Separador */}
        <div className="border-t border-stone-800" />

        {/* Filtro por score */}
        <ScoreFilter
          minScore={filters.minScore}
          maxScore={filters.maxScore}
          onChange={handleScoreChange}
        />

        {/* Estadísticas */}
        {showStats && (
          <>
            <div className="border-t border-stone-800" />
            <FilterStats
              stats={stats}
              onReset={handleReset}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default FilterPanel;
